import { FilterGroupCard } from "@/components/campaign/basic/FilterGroupCard";
import { TargetGroupCount } from "@/components/configuration/TargetGroupCount";
import { FilterCategories as FilterCategoriesComponent } from "@/components/filter-groups/FilterCategories";
import { Input } from "@/components/generic/Field";
import {
  filterCategories,
  groupFiltersByFilterGroup,
} from "@/data/filters/config";
import { TypedFilterGroup } from "@/data/filters/filter-types";
import { useTargetGroup } from "@/providers/TargetGroupProvider";
import {
  getConfigurationSuggestions,
  useConfigurationCounts,
} from "@/queries/configurations";
import { trackEvent } from "@/services/amplitude.service";
import { getConfigurationFilterCount } from "@/utils/configurations";
import { buildFilterGroupFromLabelInfo } from "@/utils/filter-utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, ToOptions } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import isEmpty from "lodash-es/isEmpty";
import { Funnel, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button as RACButton } from "react-aria-components";

export const Route = createFileRoute("/_dashboard/campaigns/setup/define/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { appConfiguration, auth, useNewCampaignState } =
    Route.useRouteContext();

  const [newCampaignState] = useNewCampaignState();
  const { showSidebar } = useTargetGroup();

  const goldConfig = useMemo(() => {
    return newCampaignState.configurations.find((c) => c.quality === 1);
  }, [newCampaignState.configurations]);

  const hasConfigsEnabled = useMemo(
    () =>
      newCampaignState.configurations
        .filter((c) => c.quality !== 1)
        .some((c) => c.enabled),
    [newCampaignState.configurations],
  );

  const isExpansionAvailable = useMemo(
    () => !newCampaignState.configurations.every((c) => c.enabled),
    [newCampaignState.configurations],
  );

  const { data: suggestedConfigs, isFetching: isLoadingSuggestions } = useQuery(
    getConfigurationSuggestions({
      configuration: goldConfig,
    }),
  );

  const potentialLeadCount = useMemo(() => {
    if (!suggestedConfigs || suggestedConfigs.length === 0) return null;

    return suggestedConfigs
      .filter(
        (s) =>
          !newCampaignState.configurations.some(
            (c) => c.quality === s.configuration.quality && c.enabled,
          ),
      )
      .reduce((sum, config) => {
        if (config.configuration.quality === 1) return 0;
        return sum + config.count;
      }, 0);
  }, [suggestedConfigs, newCampaignState.configurations]);
  const { data: configCounts, isLoading: isFetchingCounts } =
    useConfigurationCounts({
      configurations: newCampaignState.configurations,
      stopListIds:
        newCampaignState.stopLists?.map((s) => s.uniqueId.toString()) ?? [],
    });
  const targetGroupCount = useMemo(() => {
    const totalCounts =
      configCounts?.reduce((sum, count) => sum + count.count, 0) ?? 0;
    return totalCounts;
  }, [configCounts]);

  const addedFilterGroups = useMemo(() => {
    const groupedFilters = groupFiltersByFilterGroup(
      newCampaignState.configurations.at(0)?.query.filters ?? [],
    );
    const groupIds = new Set<string>();
    for (const { filterGroup } of groupedFilters) {
      groupIds.add(filterGroup.id);
    }
    return groupIds;
  }, [newCampaignState.configurations]);
  const queryAmount = useMemo(() => {
    // Return amount of queries from base configuration (quality 1 and enabled)
    const baseConfig = newCampaignState.configurations.find(
      (config) => config.priority === 1 && config.enabled,
    );
    return baseConfig ? getConfigurationFilterCount(baseConfig) : 0;
  }, [newCampaignState.configurations]);

  //#region Search
  const [searchValue, setSearchValue] = useState("");
  const trimmedSearchValue = useMemo(() => searchValue.trim(), [searchValue]);
  const debouncedSearchValue = useDebounce(trimmedSearchValue, 300);

  const debouncedAmplitudeSearchValue = useDebounce(trimmedSearchValue, 1000);
  useEffect(() => {
    if (isEmpty(debouncedSearchValue)) return;

    trackEvent("Searched Filters", auth.user.id, {
      filterResults: filteredFilters.length,
      query: debouncedAmplitudeSearchValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAmplitudeSearchValue]);

  const allFilters = useMemo(() => {
    const filters = new Map<string, TypedFilterGroup & { category: string }>();

    buildFilterGroupFromLabelInfo(appConfiguration?.labelFilters || []).forEach(
      (filterGroup) => {
        const key = `${filterGroup.title} ${filterGroup.keywords.join(" ")}`;
        filters.set(key, {
          ...filterGroup,
          category: "custom",
        });
      },
    );

    for (const [categoryKey, category] of Object.entries(filterCategories)) {
      if (categoryKey === "contacts") {
        filters.set("kontaktpersoner stilling personale rolle titel", {
          title: "Kontaktpersoner",
          id: category.id,
          description: category.description,
          isAdvancedFilter:
            "isAdvancedFilter" in category ? category.isAdvancedFilter : false,
          category: categoryKey,
        } as TypedFilterGroup & { category: string });
        continue;
      }
      for (const filterGroup of Object.values(
        category.filters,
      ) as TypedFilterGroup[]) {
        const key = `${filterGroup.title.toLowerCase()} ${filterGroup.keywords.join(" ") || ""}`;
        filters.set(key, {
          isAdvancedFilter:
            "isAdvancedFilter" in category
              ? category.isAdvancedFilter
              : filterGroup.isAdvancedFilter,
          ...filterGroup,
          category: categoryKey,
        });
      }
    }

    return filters;
  }, [appConfiguration?.labelFilters]);

  const filteredFilters = useMemo(() => {
    if (!debouncedSearchValue) return [];

    const results: (TypedFilterGroup & { category: string })[] = [];
    for (const [key, filterGroup] of allFilters) {
      if (key.toLowerCase().includes(debouncedSearchValue.toLowerCase())) {
        results.push(filterGroup);
      }
    }

    return results.sort((a, b) => a.title.localeCompare(b.title, "da-DK"));
  }, [debouncedSearchValue, allFilters]);
  //#endregion

  const buildLink = (category: string): ToOptions => {
    if (category === "contacts") {
      return {
        from: Route.fullPath,
        to: `/campaigns/setup/define/$category/contacts`,
        params: { category },
      };
    }

    return {
      from: Route.fullPath,
      to: `/campaigns/setup/define/$category`,
      params: { category },
    };
  };

  return (
    <div className="flex h-full flex-col gap-y-6">
      <div className="flex items-center justify-between">
        <TargetGroupCount
          isFetchingCounts={isFetchingCounts}
          isLoadingSuggestions={isLoadingSuggestions}
          targetGroupCount={targetGroupCount}
          potentialLeadCount={potentialLeadCount}
          hasConfigsEnabled={hasConfigsEnabled}
          isExpansionAvailable={isExpansionAvailable}
          linkProps={{
            from: Route.fullPath,
            to: "/campaigns/setup/define/advanced",
          }}
        />
        <div className="flex items-center gap-x-2">
          <AnimatePresence>
            {!isEmpty(debouncedSearchValue) && (
              <motion.div
                key="result-count"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-theme-on-background-variant ml-4 text-sm leading-0"
              >
                {filteredFilters.length}{" "}
                {filteredFilters.length === 1 ? "filter" : "filtre"} fundet
              </motion.div>
            )}
          </AnimatePresence>
          <Input
            className="shrink-0 grow"
            size="sm"
            placeholder="Søg efter filter"
            value={searchValue}
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            suffix={
              isEmpty(searchValue) ? null : (
                <RACButton onPress={() => setSearchValue("")} className="-mr-3">
                  <X size={20} />
                </RACButton>
              )
            }
          />
          <RACButton
            className="border-theme-outline-variant hover:bg-theme-on-background-hover relative shrink cursor-pointer rounded border p-4 transition-colors"
            onPress={showSidebar}
          >
            <Funnel size={20} />
            <div className="bg-theme-background absolute -top-2 -right-2 rounded-full">
              <span className="bg-theme-gradient-to/30 outline-theme-active text-theme-active flex size-4 items-center justify-center rounded-full px-1 text-xs font-medium outline">
                {queryAmount}
              </span>
            </div>
          </RACButton>
        </div>
      </div>
      <AnimatePresence>
        {!isEmpty(debouncedSearchValue) ? (
          filteredFilters.length > 0 ? (
            <motion.ul
              className="grid grid-cols-2 gap-6 xl:grid-cols-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {filteredFilters.map((filterGroup, index) => (
                <FilterGroupCard
                  key={index}
                  filterGroup={filterGroup}
                  isAdded={addedFilterGroups.has(filterGroup.id)}
                  link={{
                    from: Route.fullPath,
                    to: "/campaigns/setup/define/$category/$filter",
                    params: {
                      filter: filterGroup.id,
                      category: filterGroup.category,
                    },
                  }}
                  isAdvancedFilter={filterGroup.isAdvancedFilter}
                />
              ))}
            </motion.ul>
          ) : (
            <motion.div
              className="flex size-full shrink-0 grow items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <span className="text-theme-on-background-variant text-sm">
                Ingen filtre fundet
              </span>
            </motion.div>
          )
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FilterCategoriesComponent buildLink={buildLink} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
